import { Mp3Encoder } from "@breezystack/lamejs";

/**
 * IANA / RFC 3003 registered MIME type for MP3 (.mp1/.mp2/.mp3).
 * `audio/mp3` is a common unofficial alias and is not in the IANA registry.
 * S3 signed PUTs must use this exact value for both ContentType (signing)
 * and the fetch Content-Type header, or AWS returns SignatureDoesNotMatch.
 */
export const MP3_MIME_TYPE = "audio/mpeg";

export function float32ToInt16(buffer: Float32Array): Int16Array {
  const output = new Int16Array(buffer.length);
  for (let i = 0; i < buffer.length; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return output;
}

export async function encodePcmToMp3Blob(
  leftData: Int16Array,
  rightData: Int16Array,
  channels: number,
  sampleRate: number,
  bitrateKbps = 128,
): Promise<Blob> {
  const encoder = new Mp3Encoder(channels, sampleRate, bitrateKbps);

  const blockSize = 1152;
  const blocksPerYield = 100; // yield every ~100ms at 44100Hz
  const mp3Data: Uint8Array[] = [];

  for (let i = 0; i < leftData.length; i += blockSize) {
    const leftChunk = leftData.subarray(i, i + blockSize);
    const encoded =
      channels > 1
        ? encoder.encodeBuffer(leftChunk, rightData.subarray(i, i + blockSize))
        : encoder.encodeBuffer(leftChunk);
    if (encoded.length > 0) mp3Data.push(encoded);

    if ((i / blockSize) % blocksPerYield === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  const flushed = encoder.flush();
  if (flushed.length > 0) mp3Data.push(flushed);

  return new Blob(mp3Data as BlobPart[], { type: MP3_MIME_TYPE });
}

export function createMp3File(audioBlob: Blob, fileName: string): File {
  return new File([audioBlob], fileName, { type: MP3_MIME_TYPE });
}

export function getMp3PutObjectContentType(): string {
  return MP3_MIME_TYPE;
}
